import React, { createContext, useContext, useState, useCallback } from 'react'
import { useNotification } from '../NotificationSystem'

const DemoContext = createContext(null)

// Simulated agent activities and tasks
const simulatedTasks = {
  coder: [
    'Initializing GitHub repository',
    'Setting up project structure',
    'Implementing core functionality',
    'Writing unit tests',
    'Optimizing performance',
    'Documenting code',
    'Reviewing pull requests'
  ],
  artist: [
    'Creating wireframes',
    'Designing UI components',
    'Generating visual assets',
    'Optimizing layouts',
    'Creating animations',
    'Implementing responsive design'
  ],
  social: [
    'Analyzing engagement metrics',
    'Drafting content strategy',
    'Scheduling posts',
    'Monitoring hashtag performance',
    'Generating engagement reports',
    'Optimizing content timing'
  ]
}

// Demo project templates
const projectTemplates = [
  {
    name: 'Social Media Dashboard',
    type: 'x_management',
    description: 'AI-powered social media management platform with real-time analytics.',
    agents: { coders: 3, artists: 1, social: 2 }
  },
  {
    name: 'E-commerce Website',
    type: 'website',
    description: 'Modern e-commerce platform with AI-powered recommendations.',
    agents: { coders: 5, artists: 2, social: 1 }
  },
  {
    name: 'Developer SDK',
    type: 'github',
    description: 'Advanced SDK for integrating AI capabilities into applications.',
    agents: { coders: 4, artists: 0, social: 0 }
  }
]

export const DemoProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false)
  const [demoProject, setDemoProject] = useState(null)
  const [simulationProgress, setSimulationProgress] = useState(0)
  const [activeAgents, setActiveAgents] = useState([])
  const { addNotification } = useNotification()

  const enterDemoMode = useCallback(() => {
    setIsDemoMode(true)
    addNotification({
      type: 'info',
      title: 'Demo Mode Active',
      message: 'You are now in Demo Mode. All actions are simulated.',
      duration: 5000
    })
  }, [addNotification])

  const exitDemoMode = useCallback(() => {
    setIsDemoMode(false)
    setDemoProject(null)
    setSimulationProgress(0)
    setActiveAgents([])
    addNotification({
      type: 'info',
      title: 'Demo Mode Ended',
      message: 'Connect your wallet to start real projects!',
      duration: 5000
    })
  }, [addNotification])

  const getRandomTask = useCallback((agentType) => {
    const tasks = simulatedTasks[agentType]
    return tasks[Math.floor(Math.random() * tasks.length)]
  }, [])

  const createDemoProject = useCallback((template = projectTemplates[0]) => {
    const newProject = {
      id: Date.now(),
      ...template,
      status: 'draft',
      date: new Date().toISOString(),
      deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      progress: 0,
      activeAgents: []
    }
    setDemoProject(newProject)
    return newProject
  }, [])

  const deployDemoProject = useCallback(async () => {
    if (!demoProject) return

    // Initialize agents
    const newAgents = []
    Object.entries(demoProject.agents).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        newAgents.push({
          id: `${type}-${i + 1}`,
          type: type.slice(0, -1), // remove 's' from plural
          name: `${type.charAt(0).toUpperCase() + type.slice(1, -1)}Bot-${i + 1}`,
          currentTask: getRandomTask(type.slice(0, -1)),
          status: 'active'
        })
      }
    })
    setActiveAgents(newAgents)

    // Simulate deployment progress
    setDemoProject(prev => ({ ...prev, status: 'active' }))
    
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 500))
      setSimulationProgress(i)
      
      // Update agent tasks periodically
      if (i % 30 === 0) {
        setActiveAgents(prev => 
          prev.map(agent => ({
            ...agent,
            currentTask: getRandomTask(agent.type)
          }))
        )
      }
    }

    addNotification({
      type: 'success',
      title: 'Demo Project Deployed',
      message: 'Agents are now simulating project activities.',
      duration: 5000
    })
  }, [demoProject, getRandomTask, addNotification])

  const pauseDemoProject = useCallback(() => {
    setDemoProject(prev => ({ ...prev, status: 'paused' }))
    addNotification({
      type: 'info',
      title: 'Demo Project Paused',
      message: 'Simulation has been paused.',
      duration: 3000
    })
  }, [addNotification])

  const resumeDemoProject = useCallback(() => {
    setDemoProject(prev => ({ ...prev, status: 'active' }))
    addNotification({
      type: 'success',
      title: 'Demo Project Resumed',
      message: 'Simulation is continuing.',
      duration: 3000
    })
  }, [addNotification])

  const value = {
    isDemoMode,
    demoProject,
    simulationProgress,
    activeAgents,
    projectTemplates,
    enterDemoMode,
    exitDemoMode,
    createDemoProject,
    deployDemoProject,
    pauseDemoProject,
    resumeDemoProject
  }

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  )
}

export const useDemo = () => {
  const context = useContext(DemoContext)
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider')
  }
  return context
}
